import { FC } from 'react';

import { isNumber } from 'lodash';
import { getCTR } from 'utils/clickme';
import { CampaignProductType } from 'utils/constants/campaign';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { VIEW_DATE_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import { Skeleton, Text, TextTypography } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { CampaignsTableColumn } from 'components/CampaignsDashboard/components/CampaignsTable/columns';
import { CampaignActions } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignActions/CampaignActions';
import { CampaignAreas } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignAreas';
import { CampaignBanners } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignBanners';
import { CampaignBudget } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignBudget';
import { CampaignName } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignName/CampaignName';
import { CampaignProfessions } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignProfessions';
import { CampaignSources } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignSources';
import { CampaignSwitch } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignSwitch';
import { CampaignTableMoney } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignTableMoney';
import { CampaignTableNumber } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignTableNumber';
import { CELL_TYPOGRAPHY, EMPTY_VALUE, MOBILE_CELL_TYPOGRAPHY } from 'components/CampaignsDashboard/utils/constants';

import { BiddingStrategy } from 'store/models/auctionData';
import { CampaignDetails, CampaignStatistics, DashboardCampaign } from 'store/models/campaignsDashboard';

const formatOptionalDate = (value: string | null): string => {
    return value ? formatDate(value, VIEW_DATE_FORMAT) : EMPTY_VALUE;
};

const formatCTR = (value: number): string => `${getCTR(value)}${NON_BREAKING_SPACE}%`;

const TrlKeys = {
    productType: {
        [CampaignProductType.Cpc]: 'CampaignsDashboard.CampaignsTable.column.productType.cpc',
        [CampaignProductType.SearchAuction]: 'CampaignsDashboard.CampaignsTable.column.productType.auction',
        // exp_PORTFOLIO-58018_next_line
        [CampaignProductType.Vr]: '',
    },
    biddingStrategy: {
        [BiddingStrategy.Auto]: 'CampaignsDashboard.CampaignsTable.column.biddingStrategy.auto',
        [BiddingStrategy.Manual]: 'CampaignsDashboard.CampaignsTable.column.biddingStrategy.manual',
    },
};

export const CampaignTableCell: FC<{
    columnKey: CampaignsTableColumn;
    campaign: DashboardCampaign;
    isStatusToggleLoading: boolean;
    onToggleStatus: () => void;
    isInsufficientBalance: boolean;
    campaignStatistics?: CampaignStatistics;
    campaignDetails?: CampaignDetails;
    isStatisticsLoading: boolean;
    isDetailsLoading: boolean;
    isMobile?: boolean;
    typography?: TextTypography;
}> = ({
    columnKey,
    campaign,
    isStatusToggleLoading,
    onToggleStatus,
    isInsufficientBalance,
    campaignStatistics,
    campaignDetails,
    isStatisticsLoading,
    isDetailsLoading,
    isMobile = false,
    typography: typographyOverride,
}) => {
    const {
        id: campaignId,
        banners,
        productType,
        areas,
        budget,
        bid,
        startAt,
        endAt,
        professionalRoles,
        sources,
        biddingStrategy,
    } = campaign;

    const { amount, averagePricePerClickInCents, clicks, shows, ctr, vacancyViews, responses, responsesBeforeClickme } =
        campaignStatistics || {};

    const trls = useTrls();
    const typography = typographyOverride ?? (isMobile ? MOBILE_CELL_TYPOGRAPHY : CELL_TYPOGRAPHY);

    const ROW_DATA = {
        [CampaignsTableColumn.Actions]: (
            <CampaignActions
                campaign={campaign}
                onToggleStatus={onToggleStatus}
                isStatusToggleLoading={isStatusToggleLoading}
            />
        ),
        [CampaignsTableColumn.Switch]: (
            <CampaignSwitch
                campaign={campaign}
                isStatusToggleLoading={isStatusToggleLoading}
                onToggleStatus={onToggleStatus}
            />
        ),
        [CampaignsTableColumn.CampaignName]: (
            <CampaignName campaign={campaign} isInsufficientBalance={isInsufficientBalance} isMobile={isMobile} />
        ),
        [CampaignsTableColumn.Banners]: (
            <CampaignBanners
                banners={banners}
                campaignId={campaignId}
                productType={productType}
                isMobile={isMobile}
                typography={typography}
            />
        ),
        [CampaignsTableColumn.ProductType]: (
            <Text maxLines={1} typography={typography}>
                {trls(TrlKeys.productType[productType])}
            </Text>
        ),
        [CampaignsTableColumn.Areas]: <CampaignAreas areas={areas} isMobile={isMobile} typography={typography} />,
        [CampaignsTableColumn.Budget]: (
            <CampaignBudget campaign={campaign} showIncreaseAction={!isMobile} typography={typography} />
        ),
        [CampaignsTableColumn.BudgetRemaining]: (
            <CampaignTableMoney
                value={isNumber(amount) && isNumber(budget) ? budget - amount : undefined}
                loading={isStatisticsLoading}
                typography={typography}
            />
        ),
        [CampaignsTableColumn.BudgetSpent]: (
            <CampaignTableMoney value={amount} loading={isStatisticsLoading} typography={typography} />
        ),
        [CampaignsTableColumn.Bid]: <CampaignTableMoney value={bid} typography={typography} />,
        [CampaignsTableColumn.AveragePricePerClick]: (
            <CampaignTableMoney
                value={averagePricePerClickInCents}
                inCents
                loading={isStatisticsLoading}
                typography={typography}
            />
        ),
        [CampaignsTableColumn.Clicks]: (
            <CampaignTableNumber value={clicks} loading={isStatisticsLoading} typography={typography} />
        ),
        [CampaignsTableColumn.Shows]: (
            <CampaignTableNumber value={shows} loading={isStatisticsLoading} typography={typography} />
        ),
        [CampaignsTableColumn.CTR]: (
            <CampaignTableNumber
                value={ctr}
                formatValue={formatCTR}
                loading={isStatisticsLoading}
                typography={typography}
            />
        ),
        [CampaignsTableColumn.VacancyViews]: (
            <CampaignTableNumber value={vacancyViews} loading={isStatisticsLoading} typography={typography} />
        ),
        [CampaignsTableColumn.Responses]: (
            <CampaignTableNumber value={responses} loading={isStatisticsLoading} typography={typography} />
        ),
        [CampaignsTableColumn.ResponsesBeforeClickme]: (
            <CampaignTableNumber value={responsesBeforeClickme} loading={isStatisticsLoading} typography={typography} />
        ),
        [CampaignsTableColumn.Sources]: <CampaignSources sources={sources} />,
        [CampaignsTableColumn.BiddingStrategy]: (
            <Text typography={typography}>
                {biddingStrategy ? trls(TrlKeys.biddingStrategy[biddingStrategy]) : EMPTY_VALUE}
            </Text>
        ),
        [CampaignsTableColumn.StartAt]: <Text typography={typography}>{formatOptionalDate(startAt)}</Text>,
        [CampaignsTableColumn.EndAt]: <Text typography={typography}>{formatOptionalDate(endAt)}</Text>,
        [CampaignsTableColumn.ProfessionalRoles]: (
            <CampaignProfessions professionalRoles={professionalRoles} isMobile={isMobile} typography={typography} />
        ),
        [CampaignsTableColumn.VacancyManager]: (
            <Text typography={typography}>{campaignDetails?.vacancyManagerName ?? EMPTY_VALUE}</Text>
        ),
        [CampaignsTableColumn.CreatedBy]: (
            <Text typography={typography}>
                <Skeleton height={16} width={100} borderRadius={4} loading={isDetailsLoading}>
                    {campaignDetails?.createdByName ?? EMPTY_VALUE}
                </Skeleton>
            </Text>
        ),
    };

    return ROW_DATA[columnKey];
};
